import { keepPreviousData } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import {
  Button,
  Card,
  LicensePlate,
  Pagination,
  Progress,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "@rumbledirect/ui-v2";
import type { ContractDistanceRow } from "@/contractDistance/contractDistance";
import { getDeviationTone } from "@/contractDistance/contractDistanceDeviation";
import {
  DEVIATION_TONE_CLASSES,
  formatDeviation,
  formatKilometerCount,
  formatProgress,
  getDistanceVehicleTitle,
  toProgressPercentage,
} from "@/contractDistance/contractDistanceFields";
import {
  ContractDistanceFilters,
  type ContractDistanceFilterValues,
} from "@/contractDistance/ContractDistanceFilters";
import { useGetAdminContractMonitorDistance } from "@/lib/api/generated/contract-monitor/contract-monitor";
import { cn } from "@rumbledirect/ui-v2/cn";
import { EMPTY_VALUE, formatDateOrEmpty } from "@rumbledirect/ui-v2/format";
import { toPeriodDate } from "@/lib/periodDate";

/** Matches `paginationItemsPerPage` on the backend's `ContractDistanceResource`. */
const ITEMS_PER_PAGE = 50;

/**
 * Header and responsive visibility per column, declared once so the header row,
 * the skeleton rows and the `colSpan` of the empty states cannot drift apart.
 * The trailing action column carries no header text.
 */
const COLUMNS: readonly {
  readonly id: string;
  readonly header: string;
  readonly className?: string;
}[] = [
  { id: "licensePlate", header: "Kenteken" },
  { id: "vehicle", header: "Voertuig", className: "hidden md:table-cell" },
  { id: "site", header: "Locatie", className: "hidden xl:table-cell" },
  { id: "progress", header: "Voortgang" },
  { id: "driven", header: "Gereden", className: "hidden lg:table-cell" },
  { id: "deviation", header: "Afwijking" },
  {
    id: "projected",
    header: "Prognose einde",
    className: "hidden xl:table-cell",
  },
];

const COLUMN_COUNT = COLUMNS.length + 1;

/** Enough skeleton rows to fill the fold without pretending to know the count. */
const SKELETON_ROW_KEYS = Array.from(
  { length: 10 },
  (_, index) => `contract-distance-skeleton-${index}`,
);

interface ContractDistanceSearch extends ContractDistanceFilterValues {
  readonly page: number;
}

export const Route = createFileRoute("/_authenticated/contract-distance")({
  // The whole filter state lives in the URL so a view can be shared or
  // bookmarked. Unlike the monitor and the planning there is no default period
  // to fill in: the backend reports on today when `asOf` is absent.
  validateSearch: (search: Record<string, unknown>): ContractDistanceSearch => {
    const page = Number(search.page);

    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      asOf: toPeriodDate(search.asOf),
      site:
        typeof search.site === "string" && search.site !== ""
          ? search.site
          : undefined,
    };
  },
  component: ContractDistancePage,
});

function ContractDistancePage(): ReactNode {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { page, asOf, site } = search;

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminContractMonitorDistance(
      { page, ...(asOf ? { asOf } : {}), ...(site ? { site } : {}) },
      { query: { placeholderData: keepPreviousData } },
    );

  const totalItems = data?.totalItems ?? 0;
  const members = data?.member;

  /** Any filter change resets to page 1; page 7 of the old result set is meaningless. */
  const applyFilters = (patch: Partial<ContractDistanceFilterValues>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Kilometrage</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Wat elk voertuig volgens zijn contract mag rijden, wat het gereden
          heeft, en waar dat tempo op uitkomt. Zonder kilometerstand blijft een
          waarde leeg; er wordt niets ingevuld wat niet gemeten is.
        </p>
      </div>

      <ContractDistanceFilters values={search} onChange={applyFilters} />

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              {COLUMNS.map((column) => (
                <Th key={column.id} className={column.className}>
                  {column.header}
                </Th>
              ))}
              <Th className="w-12">
                <span className="sr-only">Voertuig openen</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <ContractDistanceRows
              isPending={isPending}
              isError={isError}
              members={members}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}
    </div>
  );
}

function ContractDistanceRows({
  isPending,
  isError,
  members,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly ContractDistanceRow[] | undefined;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        {COLUMNS.map((column) => (
          <Td key={column.id} className={column.className}>
            <Skeleton className="h-4 w-20" />
          </Td>
        ))}
        <Td className="text-right">
          <Skeleton className="ml-auto h-4 w-4" />
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={COLUMN_COUNT} className="py-10 text-center">
          <div className="space-y-3" role="alert">
            <p className="text-muted-foreground">
              We konden de kilometrage niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (!members || members.length === 0) {
    return (
      <Tr>
        <Td colSpan={COLUMN_COUNT} className="py-10 text-center">
          <p className="text-muted-foreground">
            Geen lopende contracten op deze peildatum.
          </p>
        </Td>
      </Tr>
    );
  }

  return members.map((row) => (
    <ContractDistanceTableRow key={row.id} row={row} />
  ));
}

function ContractDistanceTableRow({
  row,
}: {
  readonly row: ContractDistanceRow;
}): ReactNode {
  const progressLabel = formatProgress(row.progress);
  const progressPercentage = toProgressPercentage(row.progress);
  const deviationTone = getDeviationTone(
    row.deviationDistance,
    row.allowedDistance,
  );
  const projectedTone = getDeviationTone(
    row.projectedDeviationDistance,
    row.allowedDistance,
  );

  return (
    <Tr className="group">
      <Td>
        {row.licensePlate ? (
          <LicensePlate value={row.licensePlate} />
        ) : (
          EMPTY_VALUE
        )}
      </Td>
      <Td className="hidden md:table-cell">{getDistanceVehicleTitle(row)}</Td>
      <Td className="hidden text-muted-foreground xl:table-cell">
        {row.siteName ?? EMPTY_VALUE}
      </Td>
      <Td>
        {progressPercentage === null ? (
          // An open-ended contract has no "how far along": there is no end to
          // measure against, so a bar would draw a fraction that does not exist.
          <span className="text-muted-foreground">Doorlopend</span>
        ) : (
          <div className="flex items-center gap-2">
            <Progress
              value={progressPercentage}
              className="w-24"
              aria-label={`Contractvoortgang ${row.licensePlate ?? getDistanceVehicleTitle(row)}`}
            />
            <span className="tabular-nums text-muted-foreground">
              {progressLabel}
            </span>
          </div>
        )}
      </Td>
      <Td className="hidden whitespace-nowrap lg:table-cell">
        <div className="tabular-nums">
          {formatKilometerCount(row.latestDistance)}
        </div>
        <div className="text-xs text-muted-foreground">
          op {formatDateOrEmpty(row.latestRecordedAt)}
        </div>
      </Td>
      <Td
        className={cn(
          "whitespace-nowrap tabular-nums",
          DEVIATION_TONE_CLASSES[deviationTone],
        )}
      >
        {formatDeviation(row.deviationDistance)}
      </Td>
      <Td className="hidden whitespace-nowrap xl:table-cell">
        <div className="tabular-nums">
          {formatKilometerCount(row.projectedDistance)}
        </div>
        <div
          className={cn(
            "text-xs tabular-nums",
            DEVIATION_TONE_CLASSES[projectedTone],
          )}
        >
          {formatDeviation(row.projectedDeviationDistance)} t.o.v.{" "}
          {formatKilometerCount(row.allowedDistance)}
        </div>
      </Td>
      <Td className="text-right">
        {row.vehicle && (
          <Link
            to="/vehicles/$vehicleId"
            params={{ vehicleId: row.vehicle }}
            search={{ tab: "gegevens" }}
            aria-label={`Voertuig ${row.licensePlate ?? getDistanceVehicleTitle(row)} openen`}
            className="inline-flex text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </Link>
        )}
      </Td>
    </Tr>
  );
}
